import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  Paper,
  Button,
  Stack,
  Snackbar
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";

// Assuming useServiceConfiguration and EditableDisplayTable are correctly imported
import { useServiceConfiguration, ServiceConfiguration, TemplateDetail } from "../../components/Templates/useServiceConfiguration"; // Adjust path
import EditableDisplayTable from "../../components/table/EditableDisplayTable"; // Adjust path


// --- Helper Function (can be moved or kept here) ---
// Helper to compare two detail arrays and find differences
// (Duplicate from hook - consider moving to a shared utils file)
const findDifferences = (original: TemplateDetail[], edited: TemplateDetail[]): TemplateDetail[] => {
    const changes: TemplateDetail[] = [];
    const originalMap = new Map(original.map(item => [item.key, item]));
    const editedMap = new Map(edited.map(item => [item.key, item]));

    // Check for updated and added items
    edited.forEach(editedItem => {
        const originalItem = originalMap.get(editedItem.key);
        // Ensure we don't send unchanged items
        if (!originalItem) {
            // Added item: Key is new
            changes.push({ ...editedItem });
        } else if (originalItem.value !== editedItem.value || originalItem.env !== editedItem.env) {
            // Modified item: Value or env changed
            changes.push({ ...editedItem });
        }
        // If originalItem exists and values/env are the same, it's unchanged - do nothing.
    });

    // Check for deleted items
    original.forEach(originalItem => {
        if (!editedMap.has(originalItem.key)) {
            // Deleted item: Key exists in original but not in edited
            changes.push({ ...originalItem, flag: 'delete' }); // Mark for deletion
        }
    });

    return changes;
};


// --- ServiceCard Component ---
interface ServiceCardProps {
    service: ServiceConfiguration; // Use the defined interface
    isPageEditMode: boolean;
    registerChangeCollector: (serviceId: number, collector: () => ServiceChanges | null) => void;
    unregisterChangeCollector: (serviceId: number) => void;
}

// Define structure for changes within a service
interface ServiceChanges {
    serviceId: number;
    changedClientInfo: TemplateDetail[];
    changedConfigurations: TemplateDetail[];
}


const ServiceCard = React.memo(({
    service,
    isPageEditMode,
    registerChangeCollector,
    unregisterChangeCollector
}: ServiceCardProps) => {
    // Store the editable data, initialized only when entering edit mode
    const [localEditData, setLocalEditData] = useState<{
        clientInfo: TemplateDetail[];
        configurations: TemplateDetail[];
    } | null>(null);

    const isEditingThisCard = isPageEditMode && localEditData !== null;

    useEffect(() => {
      if (isPageEditMode) {
          if (localEditData === null) {
              setLocalEditData({
                  clientInfo: service.clientInfo.map(item => ({ ...item })), 
                  configurations: service.configurations.map(item => ({ ...item })) 
              });
          }
  
      } else {
          if (localEditData !== null) {
              setLocalEditData(null);
          }
      }
  
  }, [isPageEditMode, service.clientInfo, service.configurations, service.id, localEditData]);
  
    const handleDataChange = useCallback((dataType: 'clientInfo' | 'configurations', updatedData: TemplateDetail[]) => {

      setTimeout(() => {
          setLocalEditData(prev => {
              if (!prev) return null;
              return {
                  ...prev,
                  [dataType]: [...updatedData]
              };
          });
      }, 0);
  }, []); 

    const getChanges = useCallback((): ServiceChanges | null => {
        if (!isPageEditMode || !localEditData) {
             return null;
        }

        const changedClientInfo = findDifferences(service.clientInfo, localEditData.clientInfo);
        const changedConfigurations = findDifferences(service.configurations, localEditData.configurations);

        if (changedClientInfo.length > 0 || changedConfigurations.length > 0) {
             return {
                 serviceId: service.id,
                 changedClientInfo,
                 changedConfigurations
             };
        }

        return null;

    }, [isPageEditMode, localEditData, service.id, service.clientInfo, service.configurations]);


    useEffect(() => {
        if (isPageEditMode) {
            registerChangeCollector(service.id, getChanges);
        } else {
            unregisterChangeCollector(service.id);
        }

        return () => {
            unregisterChangeCollector(service.id);
        };
    }, [isPageEditMode, service.id, registerChangeCollector, unregisterChangeCollector, getChanges]);


    const clientInfoColumns = useMemo(() => [
        { header: "Key", key: "key" },
        { header: "Value", key: "value" }
    ], []);

    const configurationColumns = useMemo(() => [
        { header: "Key", key: "key" },
        { header: "Value", key: "value" },
        {
            header: "Key Vault",
            key: "env",
            render: (value: any) => value ? <Chip size="small" label={value} color="primary" /> : <span>-</span>
        }
    ], []);

    const clientInfoData = isEditingThisCard ? localEditData.clientInfo : service.clientInfo;
    const configurationsData = isEditingThisCard ? localEditData.configurations : service.configurations;

    if (service.error) {
        return (
             <Card key={service.id} sx={{ mb: 3, borderRadius: 2 }}>
                 <CardContent>
                     <Alert severity="error">
                         Error loading service ID {service.id}: {service.error}
                     </Alert>
                 </CardContent>
             </Card>
        );
    }

    if (service.loading) { 
         return (
             <Card key={service.id} sx={{ mb: 3, borderRadius: 2 }}>
                 <CardContent><CircularProgress size={20} /> Loading Service {service.id}...</CardContent>
             </Card>
         );
    }

    return (
        <Card key={service.id} sx={{ mb: 3, borderRadius: 2, overflow: 'visible' }}>
            <CardContent>
                {/* Header Section */}
                <Box sx={{ mb: 2, overflow: 'auto' }}>
                     <Typography variant="h5" color="primary" sx={{ fontWeight: 500 }}>
                         {service.templateDetails?.Config_Nm || `Service ID: ${service.id}`}
                     </Typography>
                     <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 1 }}>
                         {/* Details like ID, Config Code etc. */}
                         <Typography variant="subtitle2" color="textSecondary"><strong>ID:</strong> {service.id}</Typography>
                         <Typography variant="subtitle2" color="textSecondary"><strong>Config Code:</strong> {service.templateDetails?.Config_Cd || "N/A"}</Typography>
                         <Typography variant="subtitle2" color="textSecondary"><strong>Type:</strong> {service.templateDetails?.ConfigService_Typ || "N/A"}</Typography>
                         <Typography variant="subtitle2" color="textSecondary"><strong>URL:</strong> {service.templateDetails?.Config_Url || "N/A"}</Typography>
                     </Box>
                 </Box>
                <Divider sx={{ mb: 2 }} />

                {/* Client Information Accordion */}
                <Accordion defaultExpanded sx={{ mb: 2, '&.MuiPaper-root': { boxShadow: 'none', border: '1px solid rgba(0, 0, 0, 0.12)' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>
                        <Typography variant="h6">Client Information</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        {clientInfoData.length > 0 || isEditingThisCard ? ( 
                            <EditableDisplayTable
                                columns={clientInfoColumns}
                                data={clientInfoData}
                                loading={false}
                                isEditable={false}
                                onDataChange={(updatedData) => handleDataChange('clientInfo', updatedData)}
                            />
                        ) : (
                            <Alert severity="info">No client information available</Alert>
                        )}
                    </AccordionDetails>
                </Accordion>

                {/* Service Configurations Accordion */}
                <Accordion defaultExpanded sx={{ '&.MuiPaper-root': { boxShadow: 'none', border: '1px solid rgba(0, 0, 0, 0.12)' } }}>
                     <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>
                         <Typography variant="h6">Service Configurations</Typography>
                     </AccordionSummary>
                     <AccordionDetails>
                         {configurationsData.length > 0 || isEditingThisCard ? (
                             <EditableDisplayTable
                                 columns={configurationColumns}
                                 data={configurationsData}
                                 loading={false}
                                 isEditable={isEditingThisCard}
                                 onDataChange={(updatedData) => handleDataChange('configurations', updatedData)}
                             />
                         ) : (
                             <Alert severity="info">No configuration details available</Alert>
                         )}
                     </AccordionDetails>
                 </Accordion>
            </CardContent>
        </Card>
    );
});

// --- ServiceConfigurationView Component ---
const ServiceConfigurationView: React.FC = () => {
  const { serviceConfigurations, isLoading, error, saveTemplateChanges } = useServiceConfiguration();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: "", severity: "success" as "success" | "error" | "info" });

  // Ref to store the change collector functions from each ServiceCard
  const changeCollectorsRef = useRef<Map<number, () => ServiceChanges | null>>(new Map());

  // Callback for ServiceCard to register its change collector
  const registerChangeCollector = useCallback((serviceId: number, collector: () => ServiceChanges | null) => {
    changeCollectorsRef.current.set(serviceId, collector);
  }, []);

  // Callback for ServiceCard to unregister its change collector
  const unregisterChangeCollector = useCallback((serviceId: number) => {
    changeCollectorsRef.current.delete(serviceId);
  }, []);

  // Handle saving all changes
  const handleSaveChanges = useCallback(async () => {
    setIsSaving(true);
    try {
      // Collect changes from all registered collectors
      const allChanges: ServiceChanges[] = [];
      changeCollectorsRef.current.forEach(collector => {
        const changes = collector(); // Execute the collector function
        if (changes) { // Only add if there are actual changes
          allChanges.push(changes);
        }
      });

      // If no changes collected, inform the user and exit early
      if (allChanges.length === 0) {
          setNotification({
              open: true,
              message: "No changes were made.",
              severity: "info"
          });
          setIsEditMode(false); // Exit edit mode
          changeCollectorsRef.current.clear(); // Clear collectors
          setIsSaving(false);
          return;
      }


      // Call the hook's save function with the collected differences
      const response = await saveTemplateChanges(allChanges, serviceConfigurations[0]!.templateDetails!.Config_Cd, '');

      // Check response from the hook's save function
      if (response.success) {
          setNotification({
             open: true,
             message: response.message || "Changes saved successfully!", // Use message from response if available
             severity: "success"
          });
          setIsEditMode(false); // Exit edit mode on success
          changeCollectorsRef.current.clear(); // Clear collectors after successful save
      } else {
         // This case might be redundant if saveTemplateChanges throws on API failure,
         // but included for robustness if it could return { success: false }
         throw new Error(response.message || "Failed to save changes.");
      }

    } catch (error: any) {
      console.error("Error saving changes:", error);
      setNotification({
        open: true,
        message: `Failed to save changes: ${error.message || 'Please try again.'}`,
        severity: "error"
      });
      // Keep edit mode active on error? Or allow cancellation? User decision.
      // setIsEditMode(true); // Stay in edit mode
    } finally {
      setIsSaving(false); // Stop saving indicator regardless of outcome
    }
  }, [saveTemplateChanges]); // Include hook function in dependencies

  // Handle cancelling the edit
  const handleCancelEdit = useCallback(() => {
    setIsEditMode(false);
    changeCollectorsRef.current.clear(); // Clear collectors on cancel
    // No need to revert data here, as localEditData in cards will be reset by useEffect
    setNotification({ open: false, message: "", severity: "success" }); // Clear any previous notification
  }, []);

  const handleCloseNotification = useCallback(() => {
    setNotification(prev => ({ ...prev, open: false }));
  }, []);

  // --- Loading and Error States ---
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading service configurations...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Paper elevation={3} sx={{ m: 2, p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Typography>An error occurred while fetching configuration data. Please check the console or try again later.</Typography>
      </Paper>
    );
  }

  // --- Main Render Logic ---
  return (
    <Box sx={{ p: 3, overflow: 'auto' }}>
      {/* Header and Edit/Save/Cancel Buttons */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 1 }}>
         <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
             <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>Template Configuration Details</Typography>
             {!isEditMode ? (
                 <Button variant="contained" color="primary" startIcon={<EditIcon />} onClick={() => setIsEditMode(true)}>
                     Edit
                 </Button>
             ) : (
                 <Stack direction="row" spacing={2}>
                     <Button variant="outlined" color="secondary" startIcon={<CancelIcon />} onClick={handleCancelEdit} disabled={isSaving}>
                         Cancel
                     </Button>
                     <Button variant="contained" color="primary" startIcon={<SaveIcon />} onClick={handleSaveChanges} disabled={isSaving}>
                         {isSaving ? "Saving..." : "Save Changes"}
                     </Button>
                 </Stack>
             )}
         </Box>
     </Paper>


      {/* Service Cards */}
      {serviceConfigurations.length === 0 && !isLoading ? ( // Check isLoading again to avoid brief flash
        <Alert severity="info">No service configurations found for the specified IDs.</Alert>
      ) : (
        serviceConfigurations.map((service) => (
          <ServiceCard
            key={service.id}
            service={service}
            isPageEditMode={isEditMode}
            registerChangeCollector={registerChangeCollector}
            unregisterChangeCollector={unregisterChangeCollector}
          />
        ))
      )}

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
       >
           <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
               {notification.message}
           </Alert>
       </Snackbar>
    </Box>
  );
};

export default ServiceConfigurationView;